/**
 * Campaign console — the owner's supervision page for every campaign the
 * seats run (`/api/v1/console`, human-only).
 *
 * Wire shapes mirror `system/services/console.py` (overview, campaign
 * detail, people, needs-you), `system/services/bundles.py :: serialize_bundle`
 * and `system/services/threads.py :: serialize_thread` — change them together.
 * Engine `waves` campaigns (B2G) take their bundles, markets, send plan and
 * suppressions from `api/b2g.ts`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AdminReviewDoc } from "./admin";
import type { HumanTask } from "./tasks";
import { api } from "./client";

export type MemberState = "planned" | "approved" | "contacted" | "in_dialogue" | "closed";
export type PersonState = MemberState | "parked";
export type ClosedAs = "qualified" | "declined" | "no_reply" | "bounced" | "unsubscribed" | "referred";
export type Turn = "us" | "them" | "christoph";
export type Phase = "sequence" | "dialogue";
export type DueSource = "legal" | "promised" | "default";
export type Engine = "waves" | "drafts";

export const MEMBER_STATES: MemberState[] = ["planned", "approved", "contacted", "in_dialogue", "closed"];

export interface Funnel {
  funnel: Record<MemberState, number>;
  /** Closed units by outcome; `other` = closed without an outcome. */
  closed: Partial<Record<ClosedAs | "other", number>>;
  parked: number;
  total: number;
}

export interface ConsoleRule {
  label: string;
  value: string;
}

export interface DoNotContact {
  who: string;
  reason: string;
  since?: string | null;
}

export interface GoalBar {
  label: string;
  current: number;
  target: number;
  unit?: string | null;
  note?: string | null;
}

export interface CampaignHead {
  slug: string;
  name: string;
  status: string;
  goal: string | null;
  engine: Engine;
  seat_slug: string;
  seat_name: string | null;
  seat_id: string | null;
  mailbox: string | null;
  delivery: "server" | "drafts";
  unit: "institutions" | "people";
  rules: ConsoleRule[];
  do_not_contact: DoNotContact[];
  goal_bar: GoalBar | null;
  opportunity_pipeline: string | null;
}

export interface CampaignRow extends Funnel {
  slug: string;
  name: string;
  status: string;
  goal: string | null;
  engine: Engine;
  seat_slug: string;
  seat_name: string | null;
  mailbox: string | null;
  delivery: "server" | "drafts";
  unit: "institutions" | "people";
  your_turn: number;
  open_bundles: number;
  /** ISO time of the next server send (waves engine), else null. */
  next_send: string | null;
}

export type NeedsYouKind = "item" | "bundle" | "wave" | "thread";

export interface NeedsYouRow {
  kind: NeedsYouKind;
  title: string;
  priority: number | null;
  seat: string | null;
  since: string | null;
  /** Site-absolute console URL. */
  href: string;
  campaign_slug: string | null;
  campaign_name: string | null;
  thread_id?: string;
  /** Set when a high-priority task arrives as an `item` row. */
  task_id?: string;
}

export interface ConsoleOverview {
  campaigns: CampaignRow[];
  needs_you: NeedsYouRow[];
  stats: {
    open_bundles: number;
    your_turn: number;
    active_campaigns: number;
    reply_rate_30d: { contacted: number; replied: number };
  };
}

export interface Participant {
  contact_id?: string | null;
  email?: string | null;
  name?: string | null;
  state: "current" | "left";
  note?: string | null;
}

export interface ThreadStep {
  at: string | null;
  channel: "email" | "linkedin" | "call" | "letter" | "note";
  dir: "in" | "out" | "note";
  subject: string | null;
  text: string;
  /** Inbound that is not the other side writing (auto-reply, bounce). */
  auto: boolean;
}

export interface ConsoleThread {
  id: string;
  campaign_slug: string | null;
  company_id: string | null;
  company_name: string | null;
  /** ISO 3166 alpha-2 of the company's country, when known. */
  market: string | null;
  contact_id: string | null;
  participants: Participant[];
  conversation_ids: string[];
  mailbox: string | null;
  subject: string | null;
  phase: Phase;
  turn: Turn;
  owner_action: string | null;
  christoph_only: boolean;
  due: { on: string; source: DueSource | null; detail: string | null } | null;
  refs: { ours: string | null; theirs: string | null };
  status: "open" | "closed";
  closed_as: ClosedAs | null;
  close_note: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  responsible_agent_id: string | null;
  responsible_agent_slug: string | null;
  outreach_anchor_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Campaign detail and thread detail only — oldest first. */
  steps?: ThreadStep[];
  /** Campaign detail only: open human tasks of the thread's current participants. */
  open_tasks?: { task_id: string; task_type: string | null; title: string | null }[];
}

export interface Person {
  contact_id: string;
  name: string;
  email: string | null;
  company_id: string | null;
  company_name: string | null;
  segment: string | null;
  state: PersonState;
  closed_as?: ClosedAs | "referred" | null;
  since: string | null;
  thread_id?: string | null;
  review_id?: string | null;
  wave_id?: string | null;
  opportunity_stage: string | null;
}

export interface BundleItem extends AdminReviewDoc {
  /** Bundle detail only: the logged message this item answers. */
  inbound?: { from: string | null; at: string | null; subject: string | null; text: string } | null;
  /** Bundle detail only: the campaign thread of the recipient. */
  thread_id?: string | null;
}

export interface Bundle {
  id: string;
  agent_id: string;
  agent_slug: string | null;
  campaign_slug: string | null;
  title: string;
  summary: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Email items per review status, plus `tasks_open` / `tasks_done`. */
  counts: Partial<Record<string, number>>;
  /** Email items. */
  item_count: number;
  expired_count: number;
  top_priority: number | null;
  state: "open" | "decided";
  /** List: bodies omitted. Detail (`/bundles/{id}`): full items. */
  items: BundleItem[];
  /** The bundle's human tasks (LinkedIn messages, calls, …), open first.
   * Older servers omit it. */
  tasks?: HumanTask[];
}

export interface AwaitingSend {
  review_id: string;
  title: string;
  to: string[];
  reviewed_at: string | null;
  bundle_id: string;
}

export interface CampaignDetail {
  campaign: CampaignHead &
    Funnel & {
      your_turn: number;
      next_send: string | null;
      opportunity_stages: { id: string; name: string; count: number }[];
    };
  threads: ConsoleThread[];
  people: Person[];
  /** Email items and tasks of the campaign; a waves campaign reads its waves from `api/b2g.ts`. */
  bundles: Bundle[];
  /** Approved items whose email has not left the mailbox yet. */
  awaiting_send: AwaitingSend[];
  results: { contacted: number; replied: number };
}

export interface ApproveRestResult {
  approved: string[];
  skipped: { id: string; reason: string }[];
  failed: { id: string; error: string }[];
}

export interface ThreadPatch {
  christoph_only?: boolean;
  /** "" clears the owner action (done). */
  owner_action?: string;
  close_as?: ClosedAs;
  close_note?: string;
  reopen?: boolean;
}

export const CONSOLE_KEY = ["console"] as const;
const POLL_MS = 30_000;

export const consoleApi = {
  /** `exclude`: campaigns left out entirely – rows, needs-you and the tiles' counts. */
  overview: (exclude: readonly string[] = []) => {
    const qs = new URLSearchParams();
    for (const slug of exclude) qs.append("exclude", slug);
    const query = qs.toString();
    return api.get<ConsoleOverview>(`/api/v1/console/campaigns${query ? `?${query}` : ""}`);
  },
  campaign: (slug: string) => api.get<CampaignDetail>(`/api/v1/console/campaigns/${encodeURIComponent(slug)}`),
  bundle: (id: string) => api.get<Bundle>(`/api/v1/console/bundles/${id}`),
  approveRest: (id: string) => api.post<ApproveRestResult>(`/api/v1/console/bundles/${id}/approve-rest`),
  thread: (id: string) => api.get<ConsoleThread>(`/api/v1/console/threads/${id}`),
  patchThread: (id: string, body: ThreadPatch) => api.patch<ConsoleThread>(`/api/v1/console/threads/${id}`, body),
};

export function useConsoleOverview(exclude: readonly string[] = []) {
  return useQuery({
    queryKey: [...CONSOLE_KEY, "overview", exclude],
    queryFn: () => consoleApi.overview(exclude),
    refetchInterval: POLL_MS,
  });
}

export function useConsoleCampaign(slug: string | undefined) {
  return useQuery({
    queryKey: [...CONSOLE_KEY, "campaign", slug],
    queryFn: () => consoleApi.campaign(slug as string),
    enabled: !!slug,
    refetchInterval: POLL_MS,
  });
}

export function useConsoleBundle(id: string | null | undefined) {
  return useQuery({
    queryKey: [...CONSOLE_KEY, "bundle", id],
    queryFn: () => consoleApi.bundle(id as string),
    enabled: !!id,
    refetchInterval: POLL_MS,
  });
}

/** Every console mutation repaints the whole console family. */
export function useConsoleInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: CONSOLE_KEY });
}

export function useApproveRest() {
  const invalidate = useConsoleInvalidate();
  return useMutation({ mutationFn: consoleApi.approveRest, onSettled: invalidate });
}

export function usePatchThread() {
  const invalidate = useConsoleInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ThreadPatch }) => consoleApi.patchThread(id, body),
    onSettled: invalidate,
  });
}
